import { Fragment } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { secondaryButton, small } from '@/components/shell/StatusScreens'
import { fichaOfPath, trailItems } from '@/components/shell/staff-nav'
import { useTrail } from '@/components/shell/trail-store'
import { cn } from '@/lib/utils'
import { erpGraphQuery } from '../api'
import type { NavEntry } from '../frentes'
import { ENTITY_META } from './entity'

/* ERP · trilha: o caminho percorrido entre telas e fichas nesta sessão (máx. 8), clicável, com "← Voltar".
   Nomes das fichas vêm do grafo (só é pedido se a trilha tem ficha; a ficha aberta já o carregou). */

export function Trail({ nav }: { nav: readonly NavEntry[] }) {
    const trail = useTrail()
    const navigate = useNavigate()
    const hasFicha = trail.some(s => fichaOfPath(s.path))
    const graph = useQuery({ ...erpGraphQuery, enabled: hasFicha }).data
    const items = trailItems(trail, nav, graph)
    const prev = items.length > 1 ? items[items.length - 2]! : null

    return (
        <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
                type="button"
                className={cn(secondaryButton, small, 'flex-none')}
                disabled={!prev}
                onClick={() => prev && void navigate({ to: prev.path as '/' })}
                aria-label={prev ? `Voltar para ${prev.label}` : 'Voltar'}
            >
                <ArrowLeft size={15} aria-hidden="true" />Voltar
            </button>
            {items.length > 0 && (
                <nav aria-label="Caminho que você percorreu" className="min-w-0 flex-1 overflow-x-auto">
                    <ol className="m-0 flex list-none items-center gap-1 p-0 text-[12.5px] whitespace-nowrap">
                        {items.map((it, i) => {
                            const last = i === items.length - 1
                            return (
                                <Fragment key={`${it.path}-${i}`}>
                                    {i > 0 && <li aria-hidden="true" className="flex-none text-[var(--line-2)]">›</li>}
                                    <li className="flex-none">
                                        <Link
                                            to={it.path as '/'}
                                            aria-current={last ? 'page' : undefined}
                                            className={cn('inline-flex min-h-7 max-w-[220px] items-center gap-1.5 rounded-[6px] px-1.5 no-underline hover:bg-[var(--hover)] hover:text-fg', last ? 'font-semibold text-fg' : 'text-muted')}
                                        >
                                            {it.type && <i aria-hidden="true" className="inline-block size-2 flex-none rounded-[2.5px]" style={{ background: ENTITY_META[it.type].color }} />}
                                            <span className="truncate">{it.label}</span>
                                        </Link>
                                    </li>
                                </Fragment>
                            )
                        })}
                    </ol>
                </nav>
            )}
        </div>
    )
}
